import {
  Line,
  ComposedChart,
  Area,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts'
import { Box, Typography } from '@mui/material'
import type { WalkTimelineRow } from '~/api/endpoints/walks'
import type { Facet } from '~/state/useChartKnobsState'
import type { Group } from './dataPipeline'
import { shortDateTick } from '~/util/date'
import { formatAxisTick, formatCompactNumber } from '~/util/numbers'
import { MESSAGES } from './chrome/chartChrome'
import { AXIS_TICK, GRID_STROKE, TOOLTIP_NUM_STYLE, X_TICK_MIN_GAP } from './chrome/chartStyle'
import { useStore } from '~/state/useStore'

interface SeriesPoint {
  time: string
  [seriesKey: string]: string | number | undefined
}

interface Props {
  rows?: WalkTimelineRow[]
  points?: SeriesPoint[]
  groups?: Group[]
  facet?: Facet
  inactiveSeries?: string[]
  yDomain?: [number, number]
  hideYAxis?: boolean
  tickCount?: number
  /** T5: overrides the x-axis tick formatter; defaults to shortDateTick. ChartCanvas supplies an
   *  FY-aware formatter only when the active display unit is 'fy' — every other unit/caller keeps
   *  shortDateTick unchanged (it is shared with ComparisonOverlayChart, which does NOT take this
   *  prop and must stay byte-identical). */
  tickFormatter?: (v: string | number) => string
}

/** Single source of truth for the series key a walk row lands in. The ratio
 *  components map (aggregateRatioRowsWithComponents) and the pivot MUST agree.
 *  Key stability: dims arrive as Postgres jsonb → key order is server-canonicalized
 *  (deterministic per fact table), so Object.values() ordering is stable. Do NOT sort
 *  here — re-ordering would CHANGE live series keys (URL-carried legend state, pins). */
export function seriesKeyForRow(r: WalkTimelineRow, showVt: boolean): string {
  const base = r.dims ? Object.values(r.dims).join(' / ') : (r.kpi_type ?? 'value')
  // ' · ' separator: kpi_type/dim values use '__' and '/', so no collision with a value_type suffix.
  return showVt && r.value_type != null ? `${base} · ${r.value_type}` : base
}

export function hasMultipleValueTypes(rows: WalkTimelineRow[]): boolean {
  const vts = new Set(rows.map((r) => r.value_type).filter((v): v is string => v != null))
  return vts.size > 1
}

/** Row 15-light: series keys with IN-WINDOW first-encounter order first, pre-window-only keys
 *  appended. The EXTENDED walk result encounters baseline rows first, which would otherwise let
 *  a pre-window-only series shift the palette slots of the in-window series vs. the flag-off
 *  rendering (window integrity for colors). showVt is decided over ALL rows — callers filter
 *  rows to in-window-derived value_types, so the suffix decision cannot differ from flag-off. */
export function seriesKeysWindowFirst(rows: WalkTimelineRow[], boundaryIso: string): string[] {
  const showVt = hasMultipleValueTypes(rows)
  const inWin: string[] = []
  const pre: string[] = []
  const seenIn = new Set<string>()
  const seenPre = new Set<string>()
  for (const r of rows) {
    const k = seriesKeyForRow(r, showVt)
    if (String(r.time) >= boundaryIso) {
      if (!seenIn.has(k)) {
        seenIn.add(k)
        inWin.push(k)
      }
    } else if (!seenPre.has(k)) {
      seenPre.add(k)
      pre.push(k)
    }
  }
  return [...inWin, ...pre.filter((k) => !seenIn.has(k))]
}

export function pivot(
  rows: WalkTimelineRow[],
  // FU-h: per-TF Dynamics passes the MERGED-rows decision so per-TF keys match the
  // merged-groups key space; omitted = self-derive (every other caller, unchanged).
  showVtOverride?: boolean,
): { points: SeriesPoint[]; seriesKeys: string[] } {
  const seriesKeys = new Set<string>()
  const byTime = new Map<string, SeriesPoint>()
  const showVt = showVtOverride ?? hasMultipleValueTypes(rows)
  for (const r of rows) {
    const seriesKey = seriesKeyForRow(r, showVt)
    seriesKeys.add(seriesKey)
    const existing = byTime.get(r.time) ?? { time: r.time }
    // null (undefined ratio) → undefined so downstream gap-handling (computeDomain / aggregates
    // skip `=== undefined`; recharts renders a gap) treats it as "no point", not 0.
    existing[seriesKey] = r.value ?? undefined
    byTime.set(r.time, existing)
  }
  const points = [...byTime.values()].sort((a, b) => String(a.time).localeCompare(String(b.time)))
  return { points, seriesKeys: [...seriesKeys] }
}

export function TimelineChart(props: Props) {
  const palette = useStore((s) => s.personal.chipPalette)
  const points: SeriesPoint[] = props.points ?? (props.rows ? pivot(props.rows).points : [])
  const groups: Group[] =
    props.groups ??
    (props.rows
      ? pivot(props.rows).seriesKeys.map((k, i) => ({
          key: k,
          label: k,
          color: { hex: palette[i % palette.length] ?? '#000000', opacity: 1 },
        }))
      : [])
  const facet: Facet = props.facet ?? 'line'
  const inactive = props.inactiveSeries ?? []
  const activeGroups = groups.filter((g) => !inactive.includes(g.key))

  if (points.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">{MESSAGES.noDataForFilters}</Typography>
      </Box>
    )
  }

  const Wrapper = ComposedChart

  return (
    <Box sx={{ width: '100%', height: 360 }}>
      <ResponsiveContainer>
        <Wrapper data={points} margin={{ top: 8, left: 24, right: 16, bottom: 24 }}>
          <CartesianGrid stroke={GRID_STROKE} vertical={false} />
          <XAxis
            dataKey="time"
            tickFormatter={props.tickFormatter ?? shortDateTick}
            minTickGap={X_TICK_MIN_GAP}
            tickCount={props.tickCount}
            tick={AXIS_TICK}
          />
          <YAxis
            hide={props.hideYAxis}
            domain={props.yDomain}
            tickFormatter={formatAxisTick}
            tick={AXIS_TICK}
          />
          <Tooltip
            formatter={(value) =>
              typeof value === 'number' ? formatCompactNumber(value) : String(value)
            }
            contentStyle={TOOLTIP_NUM_STYLE}
          />
          {activeGroups.map((g) => {
            const common = {
              dataKey: g.key,
              name: g.label,
              fill: g.color.hex,
              stroke: g.color.hex,
              isAnimationActive: false as const,
            }
            if (facet === 'line') {
              return <Line key={g.key} {...common} type="monotone" strokeWidth={2} dot={false} />
            }
            if (facet === 'stacked_area') {
              return <Area key={g.key} {...common} type="monotone" stackId="1" strokeWidth={0} />
            }
            if (facet === 'bar') {
              return <Bar key={g.key} {...common} barSize={20} />
            }
            return <Bar key={g.key} {...common} barSize={20} stackId="1" />
          })}
          {(props.yDomain?.[0] ?? 0) < 0 && <ReferenceLine y={0} />}
        </Wrapper>
      </ResponsiveContainer>
    </Box>
  )
}
